import { useCallback, useEffect, useState } from 'react'
import { Props } from './Layout'
import { useRouter } from 'next/router'
import { PagePathService } from '../../../services/PagePathService'
import { useProps as useGlobalNavigationHeaderProps } from '../../parts/commons/GlobalNavigationHeader/useProps'
import { ValidationService } from '../../../services/ValidationService'
import { useRequestWeb3ChallengeMutation } from './query.generated'
import { MetamaskService } from '@/src/services/MetamaskService'
import { useLoginUser } from '../../states/LoginUser'
import { graphqlClient } from '../../providers/graphql'
import { sendSignInLinkToEmail, signInWithCustomToken } from 'firebase/auth'
import { auth } from '@/src/services/FirebaseService'
import { LocalStorageService } from '@/src/services/LocalStorageService'
import { useFirebaseUser } from '../../states/FirebaseUser'
import { AuthService } from '@/src/services/AuthService'

export const useProps = (): Props => {
  const router = useRouter()
  const firebaseUser = useFirebaseUser().firebaseUser
  const [email, setEmail] = useState<string>('')
  const [isProcessingEmailLogin, setIsAuthenticationgEmailLogin] = useState(false)
  const [isProcessingMetamaskLogin, setIsProcessingMetamaskLogin] = useState(false)
  const disabledSubmit = !ValidationService.isValidEmail(email) || isProcessingEmailLogin
  const [requestWeb3Challenge] = useRequestWeb3ChallengeMutation()
  const { reload } = useLoginUser()

  const gotoTop = useCallback(async () => {
    const result = await auth.currentUser?.getIdTokenResult()
    if (!result?.claims?.akiverseId) return
    await reload()
    router.push(PagePathService.myPage())
  }, [router, reload])

  const onClickEmailLogin: Props['onClickEmailLogin'] = useCallback(async () => {
    setIsAuthenticationgEmailLogin(true)
    LocalStorageService.removeEmailSignin()
    LocalStorageService.removeEmailLogin()
    if (!!auth.currentUser && auth.currentUser.email === email) {
      await auth.currentUser?.getIdToken(true)
      const idTokenResult = await auth.currentUser?.getIdTokenResult()
      const akiverseId = !!idTokenResult?.claims.akiverseId
      if (!akiverseId) {
        alert('There are no users with this email address.\nPlease register at Sign up.')
        setIsAuthenticationgEmailLogin(false)
        return
      }
      const locked = !!idTokenResult?.claims.locked
      if (locked) {
        router.push(PagePathService.accountLocked())
        setIsAuthenticationgEmailLogin(false)
        return
      }
      gotoTop()
      setIsAuthenticationgEmailLogin(false)
      return
    }
    await AuthService.logOut()
    sendSignInLinkToEmail(auth, email, {
      url: process.env.NEXT_PUBLIC_FIREBASE_LOGIN_COMPLETE_URL,
      handleCodeInApp: true,
    })
      .then(() => {
        LocalStorageService.saveEmailLogin(email)
        router.push(PagePathService.mailSendComplete())
        setIsAuthenticationgEmailLogin(false)
      })
      .catch((error) => {
        console.log(error)
        alert(`Error: ${error.message}`)
        setIsAuthenticationgEmailLogin(false)
      })
  }, [email, gotoTop, router])

  const onClickMetamaskLogin: Props['onClickMetamaskLogin'] = useCallback(async () => {
    setIsProcessingMetamaskLogin(true)
    LocalStorageService.removeEmailSignin()
    LocalStorageService.removeEmailLogin()
    if (!MetamaskService.isInstalledMetamask()) {
      setIsProcessingMetamaskLogin(false)
      return alert('Please install Metamask in your browser.')
    }
    await AuthService.logOut()
    MetamaskService.getWalletAddress().then((walletAddress) => {
      if (!walletAddress) {
        setIsProcessingMetamaskLogin(false)
        return alert('Metamask account is not connected to Akiverse.')
      }
      requestWeb3Challenge({
        variables: { input: { chain: process.env.NEXT_PUBLIC_WEB3_CHALLENGE_CHAIN_ID, walletAddress } },
      }).then((res) => {
        if (res.errors) {
          setIsProcessingMetamaskLogin(false)
          alert('Login Error') //TODO
        } else {
          const message = res.data?.RequestWeb3Challenge.message ?? ''
          MetamaskService.signMessage(message)
            .then((signature) => {
              graphqlClient
                .login({ message, signature })
                .then(async ({ login: { firebaseCustomToken } }) => {
                  await signInWithCustomToken(auth, firebaseCustomToken)
                  await auth.currentUser?.getIdToken(true)
                  const idTokenResult = await auth.currentUser?.getIdTokenResult()
                  const locked = !!idTokenResult?.claims.locked
                  if (locked) {
                    router.push(PagePathService.accountLocked())
                    setIsAuthenticationgEmailLogin(false)
                    return
                  }
                  setIsProcessingMetamaskLogin(false)
                })
                .catch((error) => {
                  setIsProcessingMetamaskLogin(false)
                  const errorCode = error?.response?.errors?.[0]?.extensions?.code
                  if (errorCode === 'NOT_FOUND') {
                    // ユーザーが存在しない場合
                    alert('This Metamask is not user-linked.')
                  } else {
                    alert('Login Error') //TODO
                  }
                })
            })
            .catch(() => {
              alert('Login Error') //TODO
              setIsProcessingMetamaskLogin(false)
            })
        }
      })
    })
  }, [requestWeb3Challenge])

  useEffect(() => {
    if (firebaseUser && AuthService.isLoggedIn()) {
      gotoTop()
    }
  }, [firebaseUser, gotoTop])

  return {
    onUpdateEmail: setEmail,
    onClickEmailLogin,
    onClickMetamaskLogin,
    disabledSubmit,
    visibleEmailLoginIndicator: isProcessingEmailLogin,
    visibleMetamaskLoginIndicator: isProcessingMetamaskLogin,
    GlobalNavigationHeaderProps: useGlobalNavigationHeaderProps(),
  }
}
